import { PayloadAction, ReducerCreators } from '@reduxjs/toolkit';
import { serializeError } from '@/store/utils/serialize-error';

import { UserState } from './types';
import getUserFromToken from './helpers/get-user-from-token';
import blAuth from '@/connection/bl-auth/bl-auth';
import { clearPostSignoutItems, clearPreSignoutItems } from '@/utils/authentication/clear-auth-cache';
import toastTrigger from '@ui/toast/toast-trigger';
import { UpdateAdminProfile } from '@/connection/admins/update-admin-profile';
import { Profile } from '@/types/profile.types';
import { FeatureFlagsEnum } from '../feature-flags/types';
import { OptInBetaFeature, OptOutBetaFeature } from '@/connection/feature-flags';

let initialActiveChannel: number | undefined;

export const reducers = (create: ReducerCreators<UserState>) => ({
  setAuthTokens: create.reducer((state, action: PayloadAction<null | { idToken: string; refreshToken: string }>) => {
    if (!action.payload) {
      state.idToken = null;
      state.refreshToken = null;
      state.user = null;
      return;
    }
    state.idToken = action.payload.idToken;
    state.refreshToken = action.payload.refreshToken;
    state.user = getUserFromToken(action.payload.idToken);
    initialActiveChannel = state.user.active_channel;
  }),
  setChangingChannels: create.reducer((state, action: PayloadAction<boolean>) => {
    state.changingChannels = action.payload;
    if (state.user) {
      state.user.active_channel = initialActiveChannel as number;
    }
  }),
  signOut: create.asyncThunk(
    async () => {
      /*
          I'm not sure if clearing certain items pre/post signout is strictly necessary,
          but that's how the flow is in events-3, so I'm keeping it consistent
        */
      clearPreSignoutItems();
      return blAuth.signOut();
    },
    {
      pending: (state) => {
        state.signingOut = true;
      },
      fulfilled: (state) => {
        state.idToken = null;
        state.refreshToken = null;
        state.user = null;
        state.signingOut = false;
        clearPostSignoutItems();
        // force reload so that we navigate to the auth app with a clean slate and proper redirect urls
        window.location.reload();
      },
      rejected: (_, action) => {
        console.error(action.error);
        toastTrigger('There was an error signing out.\nPlease refresh and try again', {
          type: 'error',
        });
      },
    }
  ),
  updateAdminProfile: create.asyncThunk(
    async ({ profile }: { profile: Profile }) => {
      const res = UpdateAdminProfile(profile);
      return res;
    },
    {
      pending: (state) => {
        state.signingOut = true;
      },
      fulfilled: (state, action) => {
        if (state.user) {
          const user = getUserFromToken(action.payload.token);
          state.user.profile = user.profile;
        }
      },
      rejected: (_, action) => {
        console.error(action.error);
        toastTrigger('There was an error updating your profile.\nPlease refresh and try again', {
          type: 'error',
        });
      },
    }
  ),
  optBetaFeature: create.asyncThunk(
    async ({ feature, on }: { feature: FeatureFlagsEnum; on: boolean }, { rejectWithValue }) => {
      try {
        if (on) {
          return await OptInBetaFeature(feature);
        } else {
          return await OptOutBetaFeature(feature);
        }
      } catch (error) {
        return rejectWithValue(serializeError(error));
      }
    },
    {
      fulfilled: (state, action) => {
        const user = getUserFromToken(action.payload.token);
        blAuth.refresh();
        state.user = user;
      },
    }
  ),
  overrideCurrentChannel: create.reducer<number | undefined>((state, action) => {
    if (state.user) {
      state.user.active_channel = action.payload ?? (initialActiveChannel as number);
    }
  }),
});
